import { z } from "zod";
import { AnswerSchema, RespondentDefinitionSchema } from "@/forms/contract";
export const pillarLabels = {
  planning: "Planejamento estratégico",
  sales: "Vendas",
  marketing: "Marketing e geração de demanda",
  leadership: "Liderança e desenvolvimento de alta performance",
  management: "Gestão e governança",
};
const pillar = z.enum(["planning", "sales", "marketing", "leadership", "management"]);
const RecommendationSchema = z.object({
  courseId: z.string(),
  lessonId: z.string(),
  pillar,
  reason: z.string(),
});
const DiagnosisSchema = z.object({
  context: z.string(),
  summary: z.string(),
  pillars: z.array(
    z.object({
      pillar,
      situation: z.string(),
      strengths: z.array(z.string()),
      opportunities: z.array(z.string()),
      maturity: z.enum(["beginner", "intermediate", "advanced"]),
      evidenceQuestionIds: z.array(z.string()),
    }),
  ),
  priorities: z.array(z.object({ pillar, reason: z.string() })),
  nextSteps: z.array(z.string()),
  observations: z.string(),
  recommendations: z.array(RecommendationSchema),
  noContentReason: z.string().nullable(),
});
export const AssessmentSchema = z.object({
  id: z.string(),
  state: z.enum(["draft", "queued", "processing", "ready", "failed"]),
  required: z.boolean(),
  revision: z.number().int(),
  questionnaireVersion: z.number(),
  processingVersion: z.string(),
  definition: RespondentDefinitionSchema,
  answers: z.array(AnswerSchema),
  result: DiagnosisSchema.nullable(),
  learningPath: z
    .object({
      id: z.string(),
      noContentReason: z.string().nullable(),
      items: z.array(
        RecommendationSchema.extend({
          position: z.number(),
          courseName: z.string(),
          title: z.string(),
          completed: z.boolean(),
          percentComplete: z.number(),
        }),
      ),
    })
    .nullable(),
  completedAt: z.string().nullable().optional(),
  actions: z
    .array(
      z.object({ index: z.number().int(), title: z.string(), completedAt: z.string().nullable() }),
    )
    .optional(),
  generationAvailable: z.boolean(),
  error: z.object({ code: z.string(), message: z.string() }).nullable(),
  canRetry: z.boolean(),
  // Trilha pronta gravada sem aulas e já há conteúdo: dá para gerar de novo.
  canRegenerate: z.boolean().optional().default(false),
  retryAfter: z.string().nullable(),
});
export const OnboardingSchema = z.discriminatedUnion("state", [
  z.object({ state: z.literal("not_applicable"), required: z.literal(false) }),
  z.object({
    state: z.literal("not_started"),
    required: z.literal(true),
    questionnaireVersion: z.number(),
    definition: RespondentDefinitionSchema,
    generationAvailable: z.boolean(),
  }),
  AssessmentSchema,
]);
export const OnboardingCommandSchema = z.object({
  id: z.string(),
  state: z.string(),
  revision: z.number().optional(),
});
export type Assessment = z.infer<typeof AssessmentSchema>;
export type Diagnosis = z.infer<typeof DiagnosisSchema>;
export type Onboarding = z.infer<typeof OnboardingSchema>;

export const ActionProgressSchema = z.object({
  id: z.string(),
  actionIndex: z.number().int(),
  completedAt: z.string().nullable(),
});
